export type PeriodOption = {
  period: 'year' | 'quarter' | 'month',
  period_id: string,
  year: number,
  quarter: number,
  month: number
}

export interface PeriodOptionsDateWindow {
  minDate?: Date | string;
  maxDate?: Date | string;
}

/** 0-based start months for Q1–Q4: Jan, Apr, Jul, Oct */
const QUARTER_START_MONTH = [0, 3, 6, 9] as const;

/** 0-based end months for Q1–Q4: Mar, Jun, Sep, Dec */
const QUARTER_END_MONTH = [2, 5, 8, 11] as const;

function parsePeriodDate(value: Date | string | undefined): Date | undefined {
  if (value == null) {
    return undefined;
  }

  return value instanceof Date ? value : new Date(value);
}

function getPeriodOptionStart(option: PeriodOption): Date {
  if (option.period === 'year') {
    return new Date(option.year, 0, 1);
  }

  if (option.period === 'quarter') {
    return new Date(option.year, QUARTER_START_MONTH[option.quarter - 1], 1);
  }

  return new Date(option.year, option.month - 1, 1);
}

function getPeriodOptionEnd(option: PeriodOption): Date {
  if (option.period === 'year') {
    return new Date(option.year, 11, 31, 23, 59, 59, 999);
  }

  if (option.period === 'quarter') {
    const endMonth = QUARTER_END_MONTH[option.quarter - 1];
    return new Date(option.year, endMonth + 1, 0, 23, 59, 59, 999);
  }

  return new Date(option.year, option.month, 0, 23, 59, 59, 999);
}

export function filterPeriodOptionsByDateWindow(
  options: PeriodOption[],
  dateWindow?: PeriodOptionsDateWindow,
): PeriodOption[] {
  const minDate = parsePeriodDate(dateWindow?.minDate);
  const maxDate = parsePeriodDate(dateWindow?.maxDate);

  if (!minDate || !maxDate) {
    return options;
  }

  return options.filter(option => {
    const optionStart = getPeriodOptionStart(option);
    const optionEnd = getPeriodOptionEnd(option);
    return optionStart <= maxDate && optionEnd >= minDate;
  });
}

export function generatePeriodOptions(
  minYear: number,
  maxYear: number,
  dateWindow?: PeriodOptionsDateWindow,
): PeriodOption[] {
  const yearMap: { [k: string]: any } = {}
  const quarterYearMap: { [k: string]: any } = {};
  const monthYearMap: { [k: string]: any } = {};

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  for (let year = minYear; year <= maxYear; year++) {
    yearMap[year] = {
      label: year.toString(),
      value: year.toString(),
      period: 'year',
      period_id: year.toString(),
      year,
      year_id: year.toString(),
      quarter: null,
      quarter_id: null,
      month: null,
      month_id: null,
    }

    for (let month = 1; month <= 12; month++) {
      const quarter = Math.floor((month - 1) / 3) + 1;
      const quarterYear = quarter + '_' + year;

      if (!quarterYearMap[quarterYear]) {
        quarterYearMap[quarterYear] = {
          label: `Q${quarter}/${year}`,
          value: `Q${quarter}/${year}`,
          period: 'quarter',
          period_id: `Q${quarter}/${year}`,
          year,
          year_id: year.toString(),
          quarter,
          quarter_id: `Q${quarter}/${year}`,
          month: null,
        };
      }

      const monthYear = month + '_' + year;

      if (!monthYearMap[monthYear]) {
        monthYearMap[monthYear] = {
          label: `${monthNames[Number(month) - 1]}/${year}`,
          value: `${monthNames[Number(month) - 1]}/${year}`,
          period: 'month',
          period_id: `${monthNames[Number(month) - 1]}/${year}`,
          year,
          year_id: year.toString(),
          quarter,
          quarter_id: `Q${quarter}/${year}`,
          month,
          month_id: `${monthNames[Number(month) - 1]}/${year}`,
        };;
      }
    }
  }

  const periodOptions = [
    ...Object.values(yearMap),
    ...Object.values(quarterYearMap),
    ...Object.values(monthYearMap),
  ]

  return filterPeriodOptionsByDateWindow(periodOptions, dateWindow);
}

